import gql from 'graphql-tag';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import ITimeRange from 'types/TimeRange';

export interface ISignInAggregatedInfo {
  missingSignIns: number;
  flaggedSignIns: number;
}

const DAILY_SIGN_IN_COUNTS_QUERY = gql`
  query DailySignInCounts($startTime: Timestamp!, $endTime: Timestamp!) {
    dashboard {
      dailySignInCounts(startTime: $startTime, endTime: $endTime) {
        missingSignIns
        flaggedSignIns
      }
    }
  }
`;

export default function useDailySignInData(timeRange: ITimeRange<DateTime>, enabled: boolean) {
  const graphQLClient = useGraphQLClient();
  const [error, setError] = useState<string | null>(null);
  const [aggregatedInfo, setAggregatedInfo] = useState<ISignInAggregatedInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [refetch, setRefetch] = useState<boolean>(false);

  const startTime = timeRange.startTime.toISO({ suppressMilliseconds: true, includeOffset: false });
  const endTime = timeRange.endTime.toISO({ suppressMilliseconds: true, includeOffset: false });

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    graphQLClient
      .request<{ dashboard: { dailySignInCounts: ISignInAggregatedInfo } }>({
        document: DAILY_SIGN_IN_COUNTS_QUERY,
        variables: { startTime, endTime },
      })
      .then((result) => {
        if (cancelled) return;
        setAggregatedInfo(result.dashboard.dailySignInCounts);
        setError(null);
        setIsLoading(false);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(String(requestError));
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, startTime, endTime, refetch, graphQLClient]);

  return { aggregatedInfo, error, isLoading, setRefetch, refetch };
}

export const SIGN_IN_TIME_ENTRIES_QUERY = gql`
  query SignInTimeEntries($after: String, $filter: TimeEntryFilter, $first: Int, $sort: [TimeEntrySort!]) {
    timeEntries(after: $after, filter: $filter, first: $first, sort: $sort) {
      id
      startTime
      endTime
      updatedOn
      cursor
      member {
        id
        positionId
      }
    }
  }
`;
